import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { map } from 'rxjs/internal/operators/map';
import { from } from 'rxjs/internal/observable/from';
import { catchError } from 'rxjs/internal/operators/catchError';
import { LocationAddress } from './map.service';

@Injectable()
export class GeoCodingService {
  private readonly geocoder: google.maps.Geocoder;

  constructor() {
    this.geocoder = new google.maps.Geocoder();
  }

  /**
   * Performs reverse geocoding to get the location address (city, country, country code)
   * from the given latitude and longitude.
   *
   * @param {number} lat - The latitude of the location
   * @param {number} lng - The longitude of the location
   * @returns {Observable<LocationAddress>} An observable emitting the resolved address
   */
  reverseGeocode(lat: number, lng: number): Observable<LocationAddress> {
    return from(
      new Promise<LocationAddress>((resolve, reject) => {
        this.geocoder.geocode(
          { location: { lat, lng } },
          (results: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus) => {
            if (status === google.maps.GeocoderStatus.OK) {
              // Attempt to find relevant address components
              const cityComponent = this.findComponentByType(results, 'locality');
              const cityAltComponent = this.findComponentByType(
                results,
                'administrative_area_level_3'
              );
              const countryComponent = this.findComponentByType(results, 'country');
              const countryCodeComponent = this.findComponentByType(results, 'country');

              // Build the location address object
              const locationAddress: LocationAddress = {
                city: cityComponent?.long_name ?? cityAltComponent?.long_name,
                country: countryComponent?.long_name,
                countryCode: countryCodeComponent?.short_name,
              };

              resolve(locationAddress);
            } else {
              reject('Geocoder failed due to: ' + status);
            }
          }
        );
      })
    ).pipe(
      map((address) => address),
      catchError((error) => {
        console.error('Errore durante il geocoding:', error);
        throw error;
      })
    );
  }

  /**
   * Finds a specific address component type (e.g., 'locality', 'country') from the geocoder results.
   *
   * @param {google.maps.GeocoderResult[]} results - Array of geocoder results
   * @param {string} type - The type of component to look for
   * @returns {google.maps.GeocoderAddressComponent | null} The matching address component, or null if not found
   */
  private findComponentByType(
    results: google.maps.GeocoderResult[],
    type: string
  ): google.maps.GeocoderAddressComponent {
    for (const result of results) {
      const components = result.address_components;
      const search = components.find((component) => component.types.includes(type));
      if (search) {
        return search;
      }
    }
    return null;
  }
}
